import React from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { getBoundActions } from '../../../actions/index'
import { H1 } from '../../Heading/index'
import Button from '../../Button/index'
import IconV2, { ICON_TYPES, ICON_STYLES } from '../../Icon.v2/index'
import { BASE_PLAYLISTS, getPlaylistTileStoreKey } from '../../../services/playlist/index'
import {
  TYPE_USER_PLAYLIST_DELETE,
  TYPE_USER_PLAYLIST_RENAME,
  TYPE_USER_PLAYLIST_SHARE,
} from '../../../services/dialog/index'
import { PLAY_LIST_SHARED_GA4_EVENT } from '../../../services/event-tracking/index'
import MultiplePlaylistsShareTooltip from '../MultiplePlaylistsShareTooltip/MultiplePlaylistsShareTooltip'

const renderButton = ({
  name = 'default',
  hide,
  icon,
  text,
  onClick,
  style = ICON_STYLES.TEAL_CIRCLE,
  disabled,
  showShareTooltip = false,
} = {}) => {
  if (hide) {
    return null
  }

  return (
    <div className="multiple-playlists-header__button-container">
      <Button
        onClick={!disabled ? onClick : null}
        className={`multiple-playlists-header__button multiple-playlists-header__button--${name}`}
        disabled={disabled}
      >
        <span className="multiple-playlists-header__button-text">
          {text}
        </span>
        <IconV2
          type={icon}
          style={style}
        />
      </Button>
      {showShareTooltip && (<MultiplePlaylistsShareTooltip />)}
    </div>
  )
}

const getTitleBasePlaylist = (playlistType, staticText) => {
  const playlist = BASE_PLAYLISTS.find((pl) => pl.type === playlistType)
  if (!playlist) {
    return null
  }
  return staticText.getIn(['data', playlist.staticTextKey])
}

const MultiplePlaylistsHeader = (props) => {
  const {
    playlist,
    staticText,
    tiles,
    title,
    toggleEditMode,
    renderModal,
    resetRenameUserPlaylistError,
    sharePlaylistTooltip,
    setFeatureTrackingDataPersistent,
    auth,
    setEventDefault,
  } = props

  const activePlaylistType = playlist.getIn(['userPlaylists', 'activePlaylist', 'type'], '')
  const activePlaylistName = playlist.getIn(['userPlaylists', 'activePlaylist', 'name'])
  const storeKey = getPlaylistTileStoreKey(activePlaylistType)
  const activePlaylistTiles = tiles.get(storeKey)
  const totalTilesCount = activePlaylistTiles && activePlaylistTiles.getIn(['data', 'totalCount'], 0)
  const editMode = playlist.getIn([storeKey, 'editMode'], false)
  const isUserPlaylist = activePlaylistType.startsWith('user-')
  const hideDelete = !isUserPlaylist
  const hideRename = !isUserPlaylist
  const hideEdit = !totalTilesCount
  const hideShare = !isUserPlaylist
  const showShareTooltip = !sharePlaylistTooltip

  function trackShareEvent () {
    // GA4 event
    setEventDefault(PLAY_LIST_SHARED_GA4_EVENT.mergeDeep({
      playlist_name: activePlaylistName,
      playlist_type: 'Custom',
      ui_location: 'My Library',
    }))
  }

  const renderShareModal = () => {
    // Add event to dataLayer
    trackShareEvent()
    // Disable the Tooltip
    if (showShareTooltip) {
      setFeatureTrackingDataPersistent({
        data: Map({ sharePlaylistTooltip: true }),
        auth,
      })
    }
    renderModal(TYPE_USER_PLAYLIST_SHARE, {
      playlistName: title,
      playlistSize: totalTilesCount,
      playlistType: activePlaylistType,
    })
  }

  const renderDeleteModal = () => {
    renderModal(TYPE_USER_PLAYLIST_DELETE, {
      playlistName: title,
      playlistSize: totalTilesCount,
      playlistType: activePlaylistType,
    })
  }

  const renderRenameModal = () => {
    renderModal(TYPE_USER_PLAYLIST_RENAME, {
      playlistName: title,
      playlistType: activePlaylistType,
      onDismiss: () => resetRenameUserPlaylistError(),
    })
  }

  return (
    <header className="multiple-playlists-header">
      <H1>
        {!isUserPlaylist
          ? getTitleBasePlaylist(activePlaylistType, staticText)
          : title}
      </H1>

      <div className="multiple-playlists-header__buttons">
        {renderButton({
          name: hideEdit ? 'share-disabled' : 'share',
          hide: hideShare,
          icon: ICON_TYPES.SHARE,
          text: staticText.getIn(['data', 'shareButtonText']),
          onClick: renderShareModal,
          disabled: editMode || hideEdit,
          showShareTooltip,
        })}
        {renderButton({
          name: 'delete',
          hide: hideDelete,
          icon: ICON_TYPES.DELETE,
          text: staticText.getIn(['data', 'deleteButtonText']),
          onClick: renderDeleteModal,
          disabled: editMode,
        })}
        {renderButton({
          name: 'rename',
          hide: hideRename,
          icon: ICON_TYPES.RENAME,
          text: staticText.getIn(['data', 'renameButtonText']),
          onClick: renderRenameModal,
          disabled: editMode,
        })}
        {renderButton({
          name: 'save',
          hide: hideEdit,
          icon: editMode ? ICON_TYPES.CIRCULAR_CHECK : ICON_TYPES.PENCIL,
          text: editMode ? staticText.getIn(['data', 'saveChanges']) : staticText.getIn(['data', 'editButtonText']),
          onClick: () => toggleEditMode(storeKey),
          style: editMode ? ICON_STYLES.OUTLINE : ICON_STYLES.TEAL_CIRCLE,
        })}
      </div>
    </header>
  )
}

MultiplePlaylistsHeader.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  playlist: ImmutablePropTypes.map.isRequired,
  title: PropTypes.string.isRequired,
  tiles: ImmutablePropTypes.map.isRequired,
  sharePlaylistTooltip: PropTypes.bool,
  toggleEditMode: PropTypes.func.isRequired,
  renderModal: PropTypes.func.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  resetRenameUserPlaylistError: PropTypes.func.isRequired,
  setFeatureTrackingDataPersistent: PropTypes.func.isRequired,
  setEventDefault: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    auth: state.auth,
    playlist: state.playlist,
    staticText: state.staticText.getIn(['data', 'multiplePlaylistsHeader']),
    title: state.playlist.getIn(['userPlaylists', 'activePlaylist', 'name'], ''),
    tiles: state.tiles,
    sharePlaylistTooltip: state.featureTracking.getIn(['data', 'sharePlaylistTooltip'], false),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      toggleEditMode: actions.playlist.togglePlaylistEditMode,
      renderModal: actions.dialog.renderModal,
      resetRenameUserPlaylistError: actions.playlist.resetRenameUserPlaylistError,
      setFeatureTrackingDataPersistent:
        actions.featureTracking.setFeatureTrackingDataPersistent,
      setEventDefault: actions.eventTracking.setEventDefault,
    }
  },
)(MultiplePlaylistsHeader)
